import { Menu, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

import { NAV_SECTIONS } from '../data/sections'
import { useActiveSection } from '../hooks/use-active-section'

import { LanguageSwitcher } from './language-switcher'
import { Wordmark } from './wordmark'

const SECTION_IDS = NAV_SECTIONS.map((s) => s.id)

export function SiteHeader() {
  const { t } = useTranslation()
  const activeId = useActiveSection(SECTION_IDS)
  const [menuOpen, setMenuOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  // While the mobile panel is open it owns the screen: lock body scroll, close
  // on Escape, and move focus into the panel so keyboard users are not left
  // behind the overlay.
  useEffect(() => {
    if (!menuOpen) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeyDown)
    panelRef.current?.focus()

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  return (
    <header className="border-primary-container bg-primary fixed inset-x-0 top-0 z-50 h-20 border-b">
      <div className="max-w-content px-margin-mobile md:px-margin-tablet lg:px-margin-desktop mx-auto flex h-full items-center justify-between">
        <a
          href="#home"
          className="text-on-primary rounded-sm transition-opacity hover:opacity-80"
          onClick={() => setMenuOpen(false)}
        >
          <Wordmark />
        </a>

        <nav aria-label={t('nav.primaryLabel')} className="gap-gutter hidden items-center md:flex">
          {NAV_SECTIONS.map((section) => {
            const isActive = section.id === activeId
            return (
              <a
                key={section.id}
                href={`#${section.id}`}
                aria-current={isActive ? 'true' : undefined}
                className={cn(
                  'text-label-md border-b-2 pb-1 transition-colors duration-200',
                  isActive
                    ? 'border-secondary text-on-primary'
                    : 'text-on-primary/80 hover:text-on-primary border-transparent',
                )}
              >
                {t(section.labelKey)}
              </a>
            )
          })}
        </nav>

        <div className="gap-base flex items-center">
          <LanguageSwitcher className="hidden sm:flex" />
          <button
            type="button"
            className="text-on-primary hover:bg-on-primary/10 rounded-sm p-2 transition-colors md:hidden"
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
            aria-label={menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          id="mobile-menu"
          ref={panelRef}
          tabIndex={-1}
          className="gap-stack-lg bg-primary px-margin-mobile py-stack-lg fixed inset-0 top-20 z-40 flex flex-col overflow-y-auto outline-none md:hidden"
        >
          <nav aria-label={t('nav.primaryLabel')} className="flex flex-col">
            {NAV_SECTIONS.map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                onClick={() => setMenuOpen(false)}
                aria-current={section.id === activeId ? 'true' : undefined}
                className={cn(
                  'border-on-primary/10 text-headline-md border-b py-4 transition-colors',
                  section.id === activeId ? 'text-on-primary' : 'text-on-primary/75',
                )}
              >
                {t(section.labelKey)}
              </a>
            ))}
          </nav>
          <LanguageSwitcher className="sm:hidden" />
        </div>
      )}
    </header>
  )
}
